import { formatPrice } from '../formatting';
import getSubscriptionIntervalWithSlash from '../formatting/getSubscriptionIntervalWithSlash';
import getActivePrices from '../getActivePrices';
import { getPriceBillingConfig } from '@repo/checkout';

// Render one price's label from its own fields. Works for a `prices[]` subdoc or
// a normalized legacy shape ({ amount, currency, billingType, interval, ... }).
function renderPrice(
  { amount, currency, billingType, interval, intervalCount, payWhatYouWant },
  locale,
  options,
) {
  const formattedPrice = formatPrice({ amount, currency, locale, style: 'currency' });

  if (payWhatYouWant) {
    if (amount > 0) {
      return options.short ? `PWYW (${formattedPrice}+)` : `Pay what you want (${formattedPrice}+)`;
    }
    return `Pay what you want`;
  }

  if (billingType === 'recurring') {
    return amount > 0
      ? `${formattedPrice} ${getSubscriptionIntervalWithSlash(interval, intervalCount)}`
      : ``;
  }

  return amount > 0 ? `${formattedPrice}` : ``;
}

/**
 * The name-line price label for a product.
 *
 * - Multi-price (2+ active prices): a single price would be misleading, so show
 *   the count ("3 prices").
 * - Single active price: render that price from `prices[]` (the source of truth)
 *   — NOT the deprecated legacy `product.price`/`type`/`interval`, which drift
 *   from the real price (e.g. a product created as a subscription but later
 *   priced one-time).
 * - Legacy product with no `prices[]`: fall back to the legacy scalar fields.
 */
export default function getProductPriceLabel(product, locale, options = { short: false }) {
  const activePrices = getActivePrices(product);

  if (activePrices.length > 1) {
    return `${activePrices.length} prices`;
  }

  if (activePrices.length === 1) {
    const price = activePrices[0];
    // Nested (recurring/paymentPlan) and flat pre-migration prices read the
    // same through getPriceBillingConfig.
    const config = getPriceBillingConfig(price);
    return renderPrice(
      {
        amount: price.amount,
        // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.currency)
        currency: price.currency || product.currency,
        billingType: price.billingType,
        interval: config?.interval,
        intervalCount: config?.intervalCount,
        payWhatYouWant: price.payWhatYouWant,
      },
      locale,
      options,
    );
  }

  // Legacy product with no prices[] — fall back to the legacy scalar fields.
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run
  return renderPrice(
    {
      amount: product.price,
      currency: product.currency,
      billingType: product.type === 'subscription' ? 'recurring' : 'one_time',
      interval: product.interval,
      intervalCount: product.intervalCount,
      payWhatYouWant: product.payWhatYouWant,
    },
    locale,
    options,
  );
}
